import { useTranslation } from 'react-i18next';
import { Check, Fingerprint, Sparkles, UserCheck } from 'lucide-react';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { VoiceIdentification } from '@utils/types/organization/voice-profile.ts';
import { isHumanCorrectedSpeakerName } from '@utils/editors/voice-sample-ranges.ts';

interface Props {
  identification: VoiceIdentification;
  /** Directory name for an id, or undefined when it is no longer in the list. */
  resolveSpeakerName: (speakerId: number) => string | undefined;
  /** The speaker name currently shown on the row. */
  currentName: string;
  /** Accept: records the decision and applies the name to the document. */
  onUseSuggestion: (speakerId: number, name: string) => void;
  /** Open the full picker to choose a different person. */
  onChoosePerson: () => void;
  disabled?: boolean;
}

/** A reviewable suggestion next to the speaker label.
 *
 * Nothing is applied on its own: the chip is an offer the editor accepts, so
 * an existing correction is never silently overwritten. Once a human decision
 * exists it shows as confirmed instead of nagging again.
 *
 * `override_set` — not `reason` — decides whether a decision has been made;
 * `reason` only describes what the model produced. */
export const VoiceSuggestionChip = ({
  identification,
  resolveSpeakerName,
  currentName,
  onUseSuggestion,
  onChoosePerson,
  disabled = false
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const decided = identification.override_set;
  const suggestedId = identification.suggested_speaker_id;
  const suggestedName =
    suggestedId != null ? resolveSpeakerName(suggestedId) : undefined;

  // The identification is session-wide, keyed on the voice label — every
  // paragraph of that voice sees the same `identification`. The display name,
  // however, lives per paragraph. So "decided" here only applies if THIS
  // paragraph's name actually matches the voice-wide decision; otherwise the
  // decision is still an offer for this row.
  const chosenName =
    identification.override_speaker_id != null
      ? resolveSpeakerName(identification.override_speaker_id)
      : undefined;
  const chosenMatchesRow =
    identification.override_speaker_id === null
      ? !currentName.trim()
      : !!chosenName &&
        chosenName.trim().toLowerCase() === currentName.trim().toLowerCase();

  if (decided && chosenMatchesRow) {
    const labelText =
      identification.override_speaker_id === null
        ? t('voice-marked-unknown')
        : t('voice-assigned-to', { name: chosenName ?? t('voice-unknown') });
    return (
      <Tooltip label={t('voice-decision-recorded-hint')}>
        <button
          type="button"
          onClick={onChoosePerson}
          disabled={disabled}
          aria-label={labelText}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
          // The chip sits inline in the editor, not inside a Modal, so it
          // cannot rely on inheriting a text colour: without this it renders
          // black on the dark surface in dark mode.
          style={{
            backgroundColor: getColour('secondLayer'),
            color: getColour('normalText')
          }}
        >
          <UserCheck size={12} aria-hidden />
          <span className="max-w-32 truncate">{labelText}</span>
        </button>
      </Tooltip>
    );
  }

  // Decision exists on the backend but this paragraph still shows a different
  // name. Two sub-cases:
  //   (a) the display is a raw diarization label (SPEAKER_00) or a generic
  //       placeholder → actionable "Use Sharon" chip so the user can pull
  //       this paragraph into the voice-wide decision with one click.
  //   (b) the display is a hand-typed name that disagrees with the
  //       voice-wide decision (mis-diarized file, user renamed per-segment).
  //       Hide the chip: offering "Use Sharon" on a paragraph the user
  //       explicitly renamed to Dakota is confusing and silently overwrites
  //       their correction if clicked.
  if (decided && chosenName) {
    const applyId = identification.override_speaker_id;
    if (
      applyId != null &&
      !isHumanCorrectedSpeakerName(currentName, identification.speaker_label)
    ) {
      return (
        <Tooltip label={t('voice-use-suggestion-hint', { name: chosenName })}>
          <button
            type="button"
            onClick={() => onUseSuggestion(applyId, chosenName)}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: getColour('secondLayer'),
              color: getColour('normalText')
            }}
          >
            <Sparkles size={12} aria-hidden />
            <span className="max-w-32 truncate">
              {t('voice-use-suggestion-named', { name: chosenName })}
            </span>
          </button>
        </Tooltip>
      );
    }
    // Sub-case (b): hand-typed, doesn't match the voice-wide decision —
    // leave this paragraph alone.
    return null;
  }

  // No suggestion worth reviewing. Show a small "Assign" affordance anyway,
  // so the user has a visible entry point on every paragraph — not just the
  // ones the model matched or that have had a decision stored.
  if (!suggestedName || suggestedId == null) {
    return (
      <Tooltip label={t('voice-assign-person')}>
        <button
          type="button"
          onClick={onChoosePerson}
          disabled={disabled}
          aria-label={t('voice-assign-person')}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            backgroundColor: getColour('secondLayer'),
            color: getColour('normalText')
          }}
        >
          <Fingerprint size={12} aria-hidden />
          <span className="max-w-32 truncate">{t('voice-assign-person')}</span>
        </button>
      </Tooltip>
    );
  }

  // The row already shows this person — reviewing adds nothing, but the
  // decision is still unrecorded, so offer confirmation rather than a rename.
  const alreadyNamed =
    currentName.trim().toLowerCase() === suggestedName.trim().toLowerCase();

  return (
    <Tooltip
      label={
        alreadyNamed
          ? t('voice-confirm-identity-hint')
          : t('voice-use-suggestion-hint', { name: suggestedName })
      }
    >
      <button
        type="button"
        onClick={() => onUseSuggestion(suggestedId, suggestedName)}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          backgroundColor: getColour('secondLayer'),
          color: getColour('normalText')
        }}
      >
        {alreadyNamed ? (
          <Check size={12} aria-hidden />
        ) : (
          <Sparkles size={12} aria-hidden />
        )}
        <span className="max-w-32 truncate">
          {alreadyNamed
            ? t('voice-confirm-identity')
            : t('voice-use-suggestion-named', { name: suggestedName })}
        </span>
      </button>
    </Tooltip>
  );
};
